import { useQuery } from "@tanstack/react-query";
import type { ResponseOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import type {
  CaregiverRecentActivityChange,
  CaregiverRecentActivityResponse,
} from "./caregiverRecentActivity.types";

type CaregiverAuditResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/audit/table/caregiver/id/:caregiverId"
>;

function normalizeCaregiverRecentActivityChange(
  value: unknown
): CaregiverRecentActivityChange | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const title = Reflect.get(value, "title");

  if (typeof title !== "string") {
    return null;
  }

  const category = Reflect.get(value, "category");
  const fieldType = Reflect.get(value, "fieldType");

  return {
    category: typeof category === "string" ? category || "General Details" : "General Details",
    fieldType: typeof fieldType === "string" ? fieldType : null,
    new: Reflect.get(value, "new"),
    old: Reflect.get(value, "old"),
    title,
  };
}

function normalizeCaregiverRecentActivity(
  response: CaregiverAuditResponse
): CaregiverRecentActivityResponse {
  return {
    list: response.list.map((row) => ({
      ...row,
      changes: Array.isArray(row.changes)
        ? row.changes.flatMap((change) => {
            const normalizedChange = normalizeCaregiverRecentActivityChange(change);

            return normalizedChange === null ? [] : [normalizedChange];
          })
        : [],
      createdAt: row.updated,
      updatedAt: row.updated,
    })),
  };
}

export function useCaregiverRecentActivity(caregiverId: CaregiverProfileCaregiver["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: ["caregiverProfileModal", caregiverId, "recentActivity"],
    queryFn: () =>
      api
        .get("/agencies/:agencyId/audit/table/caregiver/id/:caregiverId", {
          path: { caregiverId },
        })
        .then(normalizeCaregiverRecentActivity),
  });
}
